/**
 * ITER296 — Compliance Intelligence
 * Read-only analytics deep-dive: repeat failures, repeat-offender outlets,
 * failure trend bars, resolution analytics, SLA dashboard, ticket ageing.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, TrendingUp, Clock, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { SectionBand, SectionTitle } from '../components/ops/SectionBand';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const TREND_PALETTE = ['var(--color-primary)', '#C9A34E', '#B45309', '#7C3AED', '#0F766E',
                       '#B91C1C', '#1D4ED8', '#334155'];

function ComplianceTrendChart({ scope = 'admin' }) {
  const { getHeaders } = useAuth();
  const [period, setPeriod] = useState('daily');
  const [groupBy, setGroupBy] = useState('outlet');
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/ops/compliance-trends?period=${period}&group_by=${groupBy}`,
        { headers: getHeaders() },
      );
      setRows(res.data?.rows || []);
      setBuckets(res.data?.buckets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load trend data');
    } finally { setLoading(false); }
  }, [period, groupBy, getHeaders]);
  useEffect(() => { load(); }, [load]);

  // Pivot rows into chart-friendly shape: [{bucket, group1: pct, group2: pct}]
  const chartData = useMemo(() => {
    const map = new Map();
    buckets.forEach((b) => map.set(b, { bucket: b }));
    rows.forEach((r) => {
      const row = map.get(r.bucket) || { bucket: r.bucket };
      row[r.group_label] = r.compliance_pct;
      map.set(r.bucket, row);
    });
    return Array.from(map.values());
  }, [rows, buckets]);

  const groups = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(r.group_label));
    return Array.from(set).slice(0, 8);   // top-8 lines per spec
  }, [rows]);

  return (
    <>
      <SectionTitle
        title="Compliance Trends"
        subtitle={`${period} · by ${groupBy} · top ${groups.length || 0} shown`}
        icon={TrendingUp}
        testId="band-compliance-trends"
        actions={
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-7 w-24 text-xs"
                              data-testid="trend-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="h-7 w-32 text-xs"
                              data-testid="trend-groupby-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outlet">By Outlet</SelectItem>
                <SelectItem value="department">By Department</SelectItem>
                <SelectItem value="checklist">By Checklist</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />
      <div className="bg-white rounded-lg border border-[#E2E8DF] p-4 mb-5"
           data-testid="compliance-trend-chart" style={{ height: 320 }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-[#5C6B64] text-sm">Loading…</div>
        ) : chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[#5C6B64] text-sm">
            No data in the selected window.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8DF" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#5C6B64' }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#5C6B64' }}
                     tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{ fontSize: 11, border: '1px solid #E2E8DF' }}
                formatter={(v) => `${v}%`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {groups.map((g, i) => (
                <Line key={g} type="monotone" dataKey={g}
                      stroke={TREND_PALETTE[i % TREND_PALETTE.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }} activeDot={{ r: 5 }}
                      connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
}

function Card({ title, children, action, testId, icon: Icon }) {
  return (
    <>
      <SectionTitle title={title} icon={Icon} actions={action}
                    testId={`band-${(title || '').toLowerCase().replace(/\s+/g, '-')}`} />
      <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 mb-5" data-testid={testId}>
        {children}
      </div>
    </>
  );
}

function Stat({ label, value, tone = '#334155' }) {
  return (
    <div className="bg-slate-50 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
        {label}
      </div>
      <div className="text-xl font-semibold mt-0.5" style={{ color: tone }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

// Simple bar-strip visualiser — no chart lib dependency, works in print too.
function TrendBars({ buckets, groups }) {
  const totals = buckets.map((b) => b.total || 0);
  const maxTotal = Math.max(1, ...totals);
  const palette = ['#0B2A5B', '#C9A34E', '#065F46', '#9A3412', '#7C3AED',
                    '#0F766E', '#B45309', '#334155', '#1D4ED8', '#B91C1C'];

  return (
    <div className="space-y-2" data-testid="trend-bars">
      {buckets.map((b) => {
        const widthPct = Math.max(2, (b.total / maxTotal) * 100);
        return (
          <div key={b.bucket} className="grid grid-cols-[100px_1fr_60px] items-center gap-2">
            <div className="text-xs font-mono text-slate-500">{b.bucket}</div>
            <div className="flex h-6 rounded overflow-hidden bg-slate-50" style={{ width: `${widthPct}%` }}>
              {groups.slice(0, 10).map((g, i) => {
                const v = b[g] || 0;
                if (v === 0) return null;
                const w = (v / b.total) * 100;
                return (
                  <div key={g} title={`${g}: ${v}`}
                        style={{ width: `${w}%`, background: palette[i % palette.length] }} />
                );
              })}
            </div>
            <div className="text-xs text-slate-700 text-right">{b.total}</div>
          </div>
        );
      })}
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          {groups.slice(0, 10).map((g, i) => (
            <div key={g} className="flex items-center gap-1.5 text-[10px] text-slate-600">
              <span className="w-2.5 h-2.5 rounded-sm inline-block"
                     style={{ background: palette[i % palette.length] }} />
              {g}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminComplianceIntelligence() {
  const { getHeaders } = useAuth();
  const [repeatFails, setRepeatFails] = useState([]);
  const [offenders, setOffenders] = useState([]);
  const [trends, setTrends] = useState({ buckets: [], groups: [] });
  const [resolution, setResolution] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('daily');
  const [groupBy, setGroupBy] = useState('outlet');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o, t, res] = await Promise.all([
        axios.get(`${API}/api/ops/repeat-failures?limit=30&days=30`,
                    { headers: getHeaders() }),
        axios.get(`${API}/api/ops/repeat-offender-outlets?days=30`,
                    { headers: getHeaders() }),
        axios.get(`${API}/api/ops/failure-trends?period=${period}&group_by=${groupBy}&days=30`,
                    { headers: getHeaders() }),
        axios.get(`${API}/api/ops/resolution-analytics?days=30`,
                    { headers: getHeaders() }),
      ]);
      setRepeatFails(r.data?.rows || []);
      setOffenders(o.data?.rows || []);
      setTrends(t.data || { buckets: [], groups: [] });
      setResolution(res.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load intelligence');
    } finally { setLoading(false); }
  }, [getHeaders, period, groupBy]);

  useEffect(() => { load(); }, [load]);

  const ageBuckets = useMemo(() => resolution?.ageing_buckets || {}, [resolution]);
  const ageBucketRows = [
    { key: '0_24h',   label: '0 – 24 hours' },
    { key: '24_48h',  label: '24 – 48 hours' },
    { key: '48_72h',  label: '48 – 72 hours' },
    { key: 'over_72h', label: '72+ hours' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="compliance-intelligence-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <SectionBand
          title="Compliance Intelligence"
          subtitle="Repeating failures, resolution performance and where to intervene next"
          icon={TrendingUp}
          testId="compliance-intel-page-band"
          actions={
            <Button variant="ghost" size="sm" onClick={load}
                    className="text-white/90 hover:text-white hover:bg-white/10">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          }
        />

        {/* ═════════════ Compliance Trend Chart — ITER297 · Part 3 ═════════════ */}
        <ComplianceTrendChart />

        {/* ═════════════ Resolution + SLA + Ageing ═════════════ */}
        <SectionTitle title="Resolution · SLA · Ageing" icon={Clock}
                      testId="band-resolution-sla" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-[#E2E8DF] p-4" data-testid="resolution-analytics">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-sm text-slate-900">Resolution (30d)</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Opened"  value={resolution?.tickets_opened} />
              <Stat label="Closed"  value={resolution?.tickets_closed} />
              <Stat label="Avg"     value={resolution?.avg_resolution_hours != null
                                             ? `${resolution.avg_resolution_hours}h` : '—'} />
              <Stat label="Fastest" value={resolution?.fastest_resolution_hours != null
                                             ? `${resolution.fastest_resolution_hours}h` : '—'} />
              <Stat label="Longest" value={resolution?.longest_resolution_hours != null
                                             ? `${resolution.longest_resolution_hours}h` : '—'} />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-[#E2E8DF] p-4" data-testid="sla-dashboard">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-slate-500" />
              <h2 className="font-semibold text-base text-slate-900">SLA Dashboard</h2>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Within SLA" value={resolution?.sla?.within} tone="#166534" />
              <Stat label="Breached"    value={resolution?.sla?.breached} tone="#B91C1C" />
              <Stat label="Critical Pending" value={resolution?.sla?.critical_pending} tone="#991B1B" />
              <Stat label="Avg Time"    value={resolution?.avg_resolution_hours != null
                                                 ? `${resolution.avg_resolution_hours}h` : '—'} />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-[#E2E8DF] p-4" data-testid="ticket-ageing">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h2 className="font-semibold text-base text-slate-900">Ticket Ageing</h2>
            </div>
            <div className="space-y-1.5">
              {ageBucketRows.map((r) => {
                const n = ageBuckets[r.key] || 0;
                const total = Object.values(ageBuckets).reduce((a, b) => a + b, 0) || 1;
                const pct = Math.round((n / total) * 100);
                return (
                  <div key={r.key} className="flex items-center gap-2">
                    <div className="text-xs text-slate-600 w-28 flex-shrink-0">{r.label}</div>
                    <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full" style={{
                        width: `${pct}%`,
                        background: r.key === 'over_72h' ? '#B91C1C' :
                                    r.key === '48_72h'   ? '#F59E0B' :
                                    r.key === '24_48h'   ? '#3B82F6' : '#10B981',
                      }} />
                    </div>
                    <div className="text-xs font-semibold text-slate-700 w-8 text-right">{n}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ═════════════ Failure Trends ═════════════ */}
        <Card
          title="Failure Trends"
          testId="failure-trends-card"
          action={
            <div className="flex gap-2">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="outlet">By Outlet</SelectItem>
                  <SelectItem value="department">By Department</SelectItem>
                  <SelectItem value="checklist">By Question</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
        >
          {trends.buckets.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No failures in the selected window.
            </div>
          ) : <TrendBars buckets={trends.buckets} groups={trends.groups} />}
        </Card>

        {/* ═════════════ Repeat Failures ═════════════ */}
        <Card title="Repeat Failure Analytics — Most-failed questions (30d)"
              testId="repeat-failures-card">
          {repeatFails.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              No failures yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    <th className="px-3 py-2">Question</th>
                    <th className="px-3 py-2">Checklist Dept</th>
                    <th className="px-3 py-2">Outlet</th>
                    <th className="px-3 py-2">Assigned To</th>
                    <th className="px-3 py-2 text-right">Failures</th>
                    <th className="px-3 py-2">Last Occurred</th>
                  </tr>
                </thead>
                <tbody>
                  {repeatFails.map((r, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="px-3 py-2 text-sm text-slate-800">{r.task_name}</td>
                      <td className="px-3 py-2 text-xs">{r.department}</td>
                      <td className="px-3 py-2 text-xs">{r.outlet_name}</td>
                      <td className="px-3 py-2 text-xs">{r.assigned_department}</td>
                      <td className="px-3 py-2 text-sm font-semibold text-right">
                        <span className={r.failure_count >= 5 ? 'text-red-700' :
                                          r.failure_count >= 3 ? 'text-amber-700' : 'text-slate-700'}>
                          {r.failure_count}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-slate-500">
                        {(r.last_occurred || '').slice(0, 16).replace('T', ' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ═════════════ Repeat Offender Outlets ═════════════ */}
        <Card title="Repeat Offender Outlets (30d)"
              testId="repeat-offenders-card">
          {offenders.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              No outlets with repeat failures.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    <th className="px-3 py-2">Outlet</th>
                    <th className="px-3 py-2 text-right">Total Failures</th>
                    <th className="px-3 py-2 text-right">Open</th>
                    <th className="px-3 py-2 text-right">Overdue</th>
                    <th className="px-3 py-2 text-right">Compliance</th>
                  </tr>
                </thead>
                <tbody>
                  {offenders.map((o) => (
                    <tr key={o.outlet_id} className="border-b last:border-b-0">
                      <td className="px-3 py-2 text-sm">{o.outlet_name}</td>
                      <td className="px-3 py-2 text-sm text-right font-semibold text-slate-800">
                        {o.total_failures}
                      </td>
                      <td className="px-3 py-2 text-sm text-right">{o.open_tickets}</td>
                      <td className="px-3 py-2 text-sm text-right font-semibold" style={{
                        color: o.overdue_tickets > 0 ? '#B91C1C' : '#334155',
                      }}>{o.overdue_tickets}</td>
                      <td className="px-3 py-2 text-sm text-right">{o.compliance_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
